import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import { Modal, Spinner } from 'reactstrap'
import { SPLIT_CASE_CONDITION_FULL, SPLIT_CASE_CONDITION_PARTIAL } from '_utils/constant'
import useDebounce from '_hooks/useDebounce'
import { editProductAction } from '_redux/modules/product'
import ButtonCommon from '../../../components/Button'
import './style.scss'

const DEFAULT_VALUES = {
  enabled: true,
  costPerItem: '',
  itemsPerCase: '',
  minItemOrder: 1,
  isFullCase: true,
  fulfillCondition: SPLIT_CASE_CONDITION_FULL,
  acknowledgementRequiredPayAfter: true,
}

export default function ModalSplitCase({ isOpen = false, toggle, row }) {
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  const location = useLocation()

  const {
    register,
    watch,
    setValue,
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({ defaultValues: DEFAULT_VALUES })

  const [isCostPerItemTouched, setIsCostPerItemTouched] = useState(false)
  const [isItemsPerCaseTouched, setIsItemsPerCaseTouched] = useState(false)

  const fulfillCondition = watch('fulfillCondition')
  const itemsPerCase = watch('itemsPerCase')
  const debouncedItemsPerCase = useDebounce(itemsPerCase, 1000)

  const hideCheckbox = new URLSearchParams(location.search).has('id')
  const showCheckbox = !hideCheckbox && fulfillCondition === SPLIT_CASE_CONDITION_FULL

  useEffect(() => {
    if (isOpen && row) {
      const scp = row.splitCasePurchase
      reset({
        enabled: scp?.enabled ?? true,
        costPerItem: scp?.costPerItem ?? '',
        itemsPerCase: scp?.itemsPerCase ?? '',
        minItemOrder: scp?.minItemOrder ?? 1,
        isFullCase: scp?.fulfillCondition === SPLIT_CASE_CONDITION_FULL,
        fulfillCondition: scp?.fulfillCondition ?? SPLIT_CASE_CONDITION_FULL,
        acknowledgementRequiredPayAfter: scp?.acknowledgementRequiredPayAfter ?? true,
      })
      setIsCostPerItemTouched(false)
      setIsItemsPerCaseTouched(false)
    }
  }, [isOpen, row, reset])

  useEffect(() => {
    setValue('acknowledgementRequiredPayAfter', fulfillCondition === SPLIT_CASE_CONDITION_FULL)
    setValue('isFullCase', fulfillCondition === SPLIT_CASE_CONDITION_FULL)
  }, [fulfillCondition, setValue])

  useEffect(() => {
    const price = Number(row?.price)
    const items = Number(debouncedItemsPerCase)
    const initialCostPerItem = row?.splitCasePurchase?.costPerItem

    if (initialCostPerItem !== null && initialCostPerItem !== undefined && initialCostPerItem !== '') {
      if (!isItemsPerCaseTouched) {
        return
      }
    }

    if (isOpen && !isCostPerItemTouched && items > 0 && price > 0) {
      setValue('costPerItem', Number((price / items).toFixed(2)))
    }
  }, [
    isOpen,
    debouncedItemsPerCase,
    row?.price,
    row?.splitCasePurchase?.costPerItem,
    setValue,
    isCostPerItemTouched,
    isItemsPerCaseTouched,
  ])

  const onSubmit = async (data) => {
    if (!row) {
      toggle()
      return
    }

    const submitData = {
      pSplitCasePurchase: true,
      splitCasePurchase: {
        enabled: data.enabled,
        name: row?.name ?? '',
        description: row?.description ?? '',
        costPerItem: Number(data.costPerItem) || 0,
        itemsPerCase: Number(data.itemsPerCase) || 0,
        minItemOrder: Number(data.minItemOrder) || 1,
        isFullCase: data.fulfillCondition === SPLIT_CASE_CONDITION_FULL,
        fulfillCondition: data.fulfillCondition,
        acknowledgementRequiredPayAfter: data.acknowledgementRequiredPayAfter,
      },
    }

    try {
      const { msgResp } = await dispatch(editProductAction(row.id, row.eventId, submitData))
      toggle()
      addToast(msgResp, { appearance: 'success', autoDismiss: true })
    } catch (error) {
      addToast(error, { appearance: 'error', autoDismiss: true })
    }
  }

   const handleReset = async () => {
      if (!row) return
      try {
        const { msgResp } = await dispatch(
          editProductAction(row.id, row.eventId, {
            pSplitCasePurchase: false,
            splitCasePurchase: {},
          })
        )
        reset(DEFAULT_VALUES)
        setIsCostPerItemTouched(false)
        setIsItemsPerCaseTouched(false)
        toggle()
        addToast(msgResp, { appearance: 'success', autoDismiss: true })
      } catch (error) {
        addToast(error, { appearance: 'error', autoDismiss: true })
      }
    }

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered className="modal-split-case-custom">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="prod-modal-hd">
          <span className="prod-modal-title">Split Case Purchase</span>
          <button type="button" className="prod-modal-close" onClick={toggle}>
            ×
          </button>
        </div>
        <div className="prod-modal-body">
          <div
            className='text-end cursor-pointer'
            style={{ color: '#2b9797', fontSize: '14px' }}
            onClick={handleReset}
          >
            Delete / Reset
          </div>
          <div className="modal-field">
            <span>Items per case/carton</span>
            <Controller
              control={control}
              name="itemsPerCase"
              rules={{
                validate: {
                  isPositive: (v) =>
                    v === null || v === '' || Number(v) > 0 || 'Must be greater than 0',
                },
              }}
              render={({ field }) => (
                <input
                  {...field}
                  type="number"
                  placeholder="6"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    setIsItemsPerCaseTouched(true)
                    const val = e.target.value === '' ? null : Number(e.target.value)
                    field.onChange(val)
                  }}
                  onWheel={(e) => e.target.blur()}
                />
              )}
            />
          </div>
          {errors?.itemsPerCase?.message && (
            <div className="text-danger text-end" style={{ fontSize: '12px' }}>
              {errors.itemsPerCase.message}
            </div>
          )}

          <div className="modal-field">
            <span>Cost per item</span>
            <Controller
              control={control}
              name="costPerItem"
              render={({ field }) => (
                <input
                  {...field}
                  type="number"
                  placeholder="0.00"
                  step="0.50"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    setIsCostPerItemTouched(true)
                    const val = e.target.value === '' ? null : Number(e.target.value)
                    field.onChange(val)
                  }}
                  onWheel={(e) => e.target.blur()}
                />
              )}
            />
          </div>
          {errors?.costPerItem?.message && (
            <div className="text-danger text-end" style={{ fontSize: '12px' }}>
              {errors.costPerItem.message}
            </div>
          )}

          <div className="modal-field">
            <span>Minimum item order</span>
            <input
              type="number"
              {...register('minItemOrder')}
              onWheel={(e) => e.target.blur()}
            />
          </div>
          {errors?.minItemOrder?.message && (
            <div className="text-danger text-end" style={{ fontSize: '12px' }}>
              {errors.minItemOrder.message}
            </div>
          )}

          <label className="modal-radio">
            <input
              type="radio"
              value={SPLIT_CASE_CONDITION_FULL}
              {...register('fulfillCondition')}
            />
            Fulfil only when a full case is ordered
          </label>
          <label className="modal-radio">
            <input
              type="radio"
              value={SPLIT_CASE_CONDITION_PARTIAL}
              {...register('fulfillCondition')}
            />
            Fulfil even if the case is incomplete
          </label>

          {showCheckbox && (
            <label className="modal-ack">
              <input type="checkbox" checked={true} readOnly disabled />
              I acknowledge that enabling split case will automatically activate the Pay
              Later option.
            </label>
          )}
        </div>
        <div className="modal-footer">
          <ButtonCommon
            variant="outline"
            content="Cancel"
            onClick={toggle}
          />
          <ButtonCommon
            variant="primary"
            htmlType="submit"
            disabled={isSubmitting}
            icon={isSubmitting ? <Spinner size="sm" /> : null}
            content="Save"
          />
        </div>
      </form>
    </Modal>
  )
}
